import { LitElement, html, css, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { liveApi, type LiveEvent, type PresenceSnapshot, type Question } from '../api/live';
import { getToken } from '../lib/auth';
import './live-qa-panel';

/**
 * In-Present live overlay. studio-page mounts this on `document.body` when the
 * presenter clicks "Go live" while the local SlideStage Present view is on
 * screen. It owns the live SESSION lifecycle (create → end), the presence + Q&A
 * SSE stream, and the corner share panel. It does NOT render the deck — the
 * presenter's existing Present view is the slides; the host relays slide-stage
 * `onPosition` → `pushPosition()`.
 *
 * Dark, always: the Present `.viewer` is hardcoded `#0b0d10` at z-index 200, so
 * this sits at z-index 300 with literal-dark chrome AND pins
 * `data-theme="workplace-slate"` on the host so the embedded `<live-qa-panel>`'s
 * token-driven surfaces resolve dark regardless of the user's app theme.
 *
 * Session-ending is idempotent and centralised here: End-live button, host
 * removing the overlay (Exit/Esc → disconnectedCallback), and tab-close
 * (`pagehide` → keepalive beacon) all converge on `endSession()`.
 *
 * Events: `live-end` (CustomEvent, bubbles+composed) when End live is clicked.
 */
@customElement('live-present-overlay')
export class LivePresentOverlay extends LitElement {
  @property({ attribute: 'deck-id' }) deckId = '';
  @property({ attribute: 'deck-title' }) deckTitle = '';
  @state() private sessionUrl = '';
  @state() private presence: PresenceSnapshot = { count: 0, byLocale: {} };
  @state() private questions: Question[] = [];
  @state() private qaOpen = false;
  @state() private copied = false;
  @state() private panelOpen = true;
  @state() private phase: 'starting' | 'live' | 'error' = 'starting';

  private sessionId = '';
  private es: EventSource | null = null;
  private pendingPos: { slideId: string | null; step: number } | null = null;
  private liveToken = '';
  private ended = false;
  private prevTitle = '';

  static styles = css`
    :host { position: fixed; left: 16px; bottom: 16px; z-index: 300; font-size: 13px; color: #f2f0ec; }
    .panel { width: 300px; border-radius: 12px; overflow: hidden; background: #16181d;
             border: 1px solid rgba(255,255,255,0.14); box-shadow: 0 10px 36px rgba(0,0,0,0.55); }
    .head { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-bottom: 1px solid rgba(255,255,255,0.1); }
    .pill { display: inline-flex; align-items: center; gap: 6px; font-weight: 700; color: #ff5a5f; font-size: 0.72rem; letter-spacing: 0.08em; }
    .pill::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: #ff5a5f; animation: pulse 2s infinite; }
    @keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(255,90,95,0.5); } 70% { box-shadow: 0 0 0 6px rgba(255,90,95,0); } 100% { box-shadow: 0 0 0 0 rgba(255,90,95,0); } }
    .collapse { margin-left: auto; background: none; border: 1px solid rgba(255,255,255,0.18); border-radius: 6px; padding: 2px 8px; color: rgba(255,255,255,0.7); cursor: pointer; font: inherit; font-size: 0.72rem; }
    .collapse:hover { background: rgba(255,255,255,0.08); }
    .body { padding: 12px; display: flex; flex-direction: column; gap: 10px; }
    .hint { color: rgba(255,255,255,0.6); font-size: 0.78rem; }
    .url { display: flex; gap: 6px; align-items: center; }
    .url code { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; background: rgba(255,255,255,0.06); padding: 6px 8px; border-radius: 6px; color: #fff; user-select: all; }
    button.act { padding: 6px 10px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.06); color: #fff; cursor: pointer; white-space: nowrap; font: inherit; }
    button.act:hover { background: rgba(255,255,255,0.12); }
    button.copy { background: #4f7cff; border-color: #4f7cff; }
    .watching { display: flex; align-items: center; gap: 6px; }
    .watching .n { font-weight: 600; }
    .watching .langs { color: rgba(255,255,255,0.55); }
    .end { color: #ff8a8d; border-color: rgba(255,138,141,0.4); }
    .badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 999px; background: #16181d; border: 1px solid rgba(255,255,255,0.14); cursor: pointer; box-shadow: 0 6px 20px rgba(0,0,0,0.45); }
  `;

  connectedCallback() {
    super.connectedCallback();
    // Pin slate so the embedded <live-qa-panel> resolves its token surfaces dark
    // regardless of the user's app theme (it sits over the always-dark .viewer).
    this.setAttribute('data-theme', 'workplace-slate');
    this.prevTitle = document.title;
    document.title = '🔴 LIVE · ' + (this.deckTitle || 'Live presentation');
    window.addEventListener('pagehide', this.onPageHide);
    void this.start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('pagehide', this.onPageHide);
    this.es?.close();
    if (this.prevTitle) document.title = this.prevTitle;
    // Host removed us (Exit/Esc) → end the session via the normal async path.
    this.endSession(false);
  }

  private async start() {
    try {
      const session = await liveApi.create(this.deckId, ['en']);
      this.sessionId = session.sessionId;
      this.sessionUrl = `${location.origin}${session.url}`;
      this.phase = 'live';
      void this.refreshToken();
      // Replay the buffered position now the session exists (the host's first
      // onPosition fired before create() resolved).
      if (this.pendingPos?.slideId) {
        void liveApi.postPosition(this.sessionId, this.pendingPos.slideId, this.pendingPos.step).catch(() => {});
      }
      this.openStream();
    } catch {
      this.phase = 'error';
    }
  }

  /** Host calls this on each slide-stage onPosition; relays to the session, or
   *  buffers until the session is created. */
  pushPosition(p: { slideId: string | null; step: number }) {
    this.pendingPos = p;
    if (this.sessionId && p.slideId) {
      void liveApi.postPosition(this.sessionId, p.slideId, p.step).catch(() => {});
      void this.refreshToken(); // keep the pagehide beacon's token fresh
    }
  }

  private async refreshToken() {
    try { const t = await getToken(); if (t) this.liveToken = t; } catch { /* best effort */ }
  }

  /** Idempotent. viaBeacon=true uses a synchronous keepalive fetch (tab-close);
   *  otherwise the normal async setState. */
  private endSession(viaBeacon: boolean) {
    if (this.ended || !this.sessionId) return;
    this.ended = true;
    if (viaBeacon) {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (this.liveToken) headers['Authorization'] = `Bearer ${this.liveToken}`;
      try {
        void fetch(`/api/live/${encodeURIComponent(this.sessionId)}/state`, {
          method: 'POST', keepalive: true, headers, body: JSON.stringify({ status: 'ended' }),
        });
      } catch { /* best effort */ }
    } else {
      void liveApi.setState(this.sessionId, 'ended').catch(() => {});
    }
  }

  private onPageHide = () => this.endSession(true);

  private openStream() {
    this.es?.close();
    this.es = liveApi.openStream(this.sessionId);
    this.es.onmessage = (ev) => {
      try {
        const e = JSON.parse(ev.data) as LiveEvent;
        if (e.type === 'presence' && e.presence) this.presence = e.presence;
        else if (e.type === 'question') this.questions = e.questions ?? [];
      } catch { /* keepalive / malformed */ }
    };
  }

  private async copy() {
    try {
      await navigator.clipboard.writeText(this.sessionUrl);
      this.copied = true;
      window.setTimeout(() => { this.copied = false; }, 1500);
    } catch { /* clipboard blocked */ }
  }

  private end() {
    this.endSession(false);
    this.dispatchEvent(new CustomEvent('live-end', { bubbles: true, composed: true }));
  }

  private onModerate = (e: Event) => {
    const { id, status } = (e as CustomEvent<{ id: string; status: 'answered' | 'hidden' }>).detail;
    void liveApi.setQuestionStatus(this.sessionId, id, status).catch(() => {});
  };
  private onQaToggle = (e: Event) => { this.qaOpen = (e as CustomEvent<{ open: boolean }>).detail.open; };

  private langs(): string {
    return Object.entries(this.presence.byLocale)
      .sort((a, b) => b[1] - a[1])
      .map(([l, n]) => `${l.toUpperCase()} ${n}`)
      .join(' · ');
  }

  render(): TemplateResult {
    if (this.phase === 'starting') return html`<div class="badge"><span class="pill">LIVE</span> starting…</div>`;
    if (this.phase === 'error') return html`<div class="badge end" @click=${() => this.end()}>⚠ Couldn't start — dismiss</div>`;

    const langs = this.langs();
    const main = this.panelOpen
      ? html`
        <div class="panel">
          <div class="head">
            <span class="pill">LIVE</span>
            <button class="collapse" title="Hide — collapse to a small badge" @click=${() => { this.panelOpen = false; }}>Hide</button>
          </div>
          <div class="body">
            <div class="hint">Anyone with this link follows your slides in real time.</div>
            <div class="url">
              <code>${this.sessionUrl}</code>
              <button class="act copy" @click=${() => this.copy()}>${this.copied ? 'Copied ✓' : 'Copy'}</button>
            </div>
            <div class="watching">👁 <span class="n">${this.presence.count}</span> watching${langs ? html` · <span class="langs">${langs}</span>` : nothing}</div>
            <div><button class="act end" @click=${() => this.end()}>End live</button></div>
          </div>
        </div>`
      : html`<div class="badge" title="Live — click to expand" @click=${() => { this.panelOpen = true; }}>
               <span class="pill">LIVE</span> · 👁 ${this.presence.count}
             </div>`;

    return html`
      ${main}
      <live-qa-panel
        mode="presenter"
        .questions=${this.questions}
        .open=${this.qaOpen}
        @qa-moderate=${this.onModerate}
        @qa-toggle=${this.onQaToggle}
      ></live-qa-panel>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'live-present-overlay': LivePresentOverlay;
  }
}
